import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { AIChatbotMessage } from '../../models/ai-chatbot-message';
import { AiChatbotTemplateTunerGeneration } from '../../models/ai-chatbot-presentation-data';
import { AIChatbotBaseLoggingService } from '../ai-chatbot-base-logging/ai-chatbot-base-logging.service';

@Injectable({
  providedIn: 'root',
})
export class AIChatbotConversationLoggingService extends AIChatbotBaseLoggingService {
  private loggedMessageIds: Set<string> = new Set();

  constructor(
    http: HttpClient,
    userStateService: UserStateService,
    companyStateService: CompanyStateService,
  ) {
    super(http, userStateService, companyStateService);
  }

  /**
   * Logs a single message to BigQuery chats table
   * Uses composite key (sessionId + messageIdx) to reference messages
   * @param sessionId - The chat session ID
   * @param messageIdx - The index of the message in the conversation
   * @param message - The message object to log
   * @param selectedItem - Optional string describing the index of the template tuner
   *   variation that was selected at log time (null if none)
   * @param durationMs - How long the action that produced this message took, measured
   *   client-side. Only populated for assistant messages from generation or edit actions.
   * @param creditsUsed - Credits consumed by the action that produced this message
   *   (never dollars). Only populated for assistant messages from generation or edit.
   */
  logMessage(
    sessionId: string,
    messageIdx: number,
    message: AIChatbotMessage,
    selectedItem?: string | null,
    durationMs?: number,
    creditsUsed?: number,
  ): Observable<any> {
    // Prevent duplicate logging of the same message
    const messageKey = `${sessionId}_${message.id}`;
    if (this.loggedMessageIds.has(messageKey)) {
      return of(null);
    }

    // Determine what to save in the message field
    let messageText = '';

    if (message.content.message) {
      messageText = message.content.message;
    } else if (message.content.displayMessage) {
      messageText = message.content.displayMessage;
    } else if (message.content.type === 'message' && message.content.data) {
      messageText =
        typeof message.content.data === 'string' ? message.content.data : '';
    }

    const commonFields = this.buildCommonFields();
    const jsonData = {
      ...commonFields,
      ts: message.timestamp.toISOString(),
      session_id: sessionId,
      message_idx: messageIdx,
      message: messageText,
      role: message.role,
      ...(selectedItem ? { selected_item: selectedItem } : {}),
      ...(durationMs !== undefined ? { duration_ms: durationMs } : {}),
      ...(creditsUsed !== undefined ? { credits_used: creditsUsed } : {}),
    };

    return this.insertData(this.getTableUrl('chats'), jsonData).pipe(
      switchMap((response) => {
        if (response) {
          // Mark message as logged after successful insert
          this.loggedMessageIds.add(messageKey);
        }
        return of(response);
      }),
    );
  }

  /**
   * Clears the logged message IDs cache (useful when starting a new session)
   */
  clearLoggedMessages(): void {
    this.loggedMessageIds.clear();
  }

  /**
   * Logs "Edit Template" event to BigQuery chats_events table
   * @param sessionId - The chat session ID
   * @param messageIdx - The index of the related message in the chats table
   * @param prompt - The user message that triggered the edit
   * @param editedContent - The edited HTML content
   * @param changesApplied - Whether the changes were successfully applied
   * @param selectedItem - Optional string describing the index of the template tuner
   *   variation that was selected at log time (null if none)
   * @param durationMs - How long the edit action took, measured client-side from
   *   request dispatch to response receipt
   * @param creditsUsed - Credits consumed by this edit (never dollars)
   */
  logEditTemplate(
    sessionId: string,
    messageIdx: number,
    prompt: string,
    editedContent: string,
    changesApplied: boolean,
    selectedItem?: string | null,
    durationMs?: number,
    creditsUsed?: number,
  ): Observable<any> {
    const commonFields = this.buildCommonFields();
    const jsonData = {
      ...commonFields,
      session_id: sessionId,
      message_idx: messageIdx,
      role: 'assistant',
      event_type: 'edit_template',
      content: JSON.stringify({ prompt, editedContent, changesApplied }),
      ...(selectedItem ? { selected_item: selectedItem } : {}),
      ...(durationMs !== undefined ? { duration_ms: durationMs } : {}),
      ...(creditsUsed !== undefined ? { credits_used: creditsUsed } : {}),
    };

    return this.insertData(this.getTableUrl('chats_events'), jsonData);
  }

  /**
   * Logs "Create Template" event to BigQuery chats_events table
   * @param sessionId - The chat session ID
   * @param messageIdx - The index of the related message in the chats table
   * @param variations - The generated template variations
   * @param selectedItem - Optional string describing the index of the template tuner
   *   variation that was selected at log time (null if none)
   * @param durationMs - How long the generation action took, measured client-side from
   *   request dispatch to response receipt
   * @param creditsUsed - Credits consumed by this generation (never dollars)
   */
  logCreateTemplate(
    sessionId: string,
    messageIdx: number,
    variations: AiChatbotTemplateTunerGeneration[],
    selectedItem?: string | null,
    durationMs?: number,
    creditsUsed?: number,
  ): Observable<any> {
    const content = JSON.stringify(
      variations.map((variation: any, index) => ({
        prompt: variation.prompt || '',
        content: variation.content || null,
        contentUrl: variation.contentUrl || '',
        rawContentUrl: variation.rawContentUrl || '',
        enrichedPrompt: variation.enrichedPrompt ?? null,
        style: variation.style || '',
        index,
      })),
    );

    const commonFields = this.buildCommonFields();
    const jsonData = {
      ...commonFields,
      session_id: sessionId,
      message_idx: messageIdx,
      role: 'assistant',
      event_type: 'create_template',
      content,
      ...(selectedItem ? { selected_item: selectedItem } : {}),
      ...(durationMs !== undefined ? { duration_ms: durationMs } : {}),
      ...(creditsUsed !== undefined ? { credits_used: creditsUsed } : {}),
    };

    return this.insertData(this.getTableUrl('chats_events'), jsonData);
  }

  /**
   * Logs "Open in Editor" event to BigQuery chats_events table
   * @param sessionId - The chat session ID
   * @param templateHtml - The HTML template that was opened in editor
   * @param messageIdx - The index of the related message in the chats table (when known)
   * @param selectedItem - Optional string describing the index of the template tuner
   *   variation that was selected at log time (null if none)
   */
  logOpenInEditor(
    sessionId: string,
    templateHtml: string,
    messageIdx?: number,
    selectedItem?: string | null,
  ): Observable<any> {
    const commonFields = this.buildCommonFields();
    const jsonData = {
      ...commonFields,
      session_id: sessionId,
      event_type: 'opened_in_editor',
      content: templateHtml,
      ...(messageIdx !== undefined ? { message_idx: messageIdx } : {}),
      ...(selectedItem ? { selected_item: selectedItem } : {}),
    };

    return this.insertData(this.getTableUrl('chats_events'), jsonData);
  }
}
